import { useState } from 'react';

import {
    IOfferEventEnrollmentSortByInput,
    IOfferEventEnrollmentSortField,
    IPortfolioReportOrderField,
    IPortfolioReportSortByInput,
    ISortDirection,
    IUserSortBy,
    IUserSortByInput,
} from 'graphql/types';

type TSortBy =
    | IUserSortBy
    | IPortfolioReportOrderField
    | IOfferEventEnrollmentSortField;

type SortByInputMap = {
    IUserSortBy: IUserSortByInput;
    IPortfolioReportOrderField: IPortfolioReportSortByInput;
    IOfferEventEnrollmentSortField: IOfferEventEnrollmentSortByInput;
};

type TSortByInput<T> = T extends keyof SortByInputMap
    ? SortByInputMap[T]
    : { field: T; direction: ISortDirection };

/**
 * This hook can be used to sort table data. The sortby data is used in queries.
 * @param sortField The field to sort by (this is dynamically set based on given type)
 * @param direction The direction to sort by, default is ascending
 * @returns An object containing the current sortBy state and a function to sort the data
 */
export const useSortBy = <T extends TSortBy>(
    sortField: T,
    direction: ISortDirection = ISortDirection.Asc
): {
    sortBy: TSortByInput<T>;
    onSort: (sortField: T, initialDirection?: ISortDirection) => void;
} => {
    const [sortBy, setSortBy] = useState({
        field: sortField,
        direction,
    } as TSortByInput<T>);

    const onSort = (sortField: T, initialDirection?: ISortDirection) => {
        const currentSortField = sortBy.field;

        let newDirection = initialDirection || ISortDirection.Asc;

        // If given field is the same as the current sort, we want to toggle the direction
        if (sortField === currentSortField) {
            switch (sortBy.direction) {
                case ISortDirection.Asc:
                    newDirection = ISortDirection.Desc;
                    break;
                case ISortDirection.AscNullsFirst:
                    newDirection = ISortDirection.DescNullsLast;
                    break;
                case ISortDirection.Desc:
                    newDirection = ISortDirection.Asc;
                    break;
                case ISortDirection.DescNullsLast:
                    newDirection = ISortDirection.AscNullsFirst;
                    break;
            }
        }
        setSortBy({
            field: sortField,
            direction: newDirection,
        } as TSortByInput<T>);
    };

    return {
        sortBy,
        onSort,
    };
};
